import React, { useState, useEffect, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  getAllRoles,
  createRole,
  updateRole,
  deleteRole,
  getRoleById,
  clearCurrentRole,
  resetCreate,
  resetUpdate,
  getActiveRoles,
} from "../slices/roleSlice";
import { usePermissions } from "../utils/permissions";
import PermissionGate from "../componets/PermissionGate";
import BottomNavbar from "../componets/BottomNavbar";

const RoleManagement = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useSelector((state) => state.user);
  const { activeRoles } = useSelector((state) => state.role);
  const { roles, loading, createLoading, updateLoading, createSuccess, updateSuccess } =
    useSelector((state) => state.role);

  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [deleteRoleId, setDeleteRoleId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    permissions: [],
    isActive: true,
  });

  // Predefined resources and actions for easy selection
  const availableResources = [
    { name: "users", label: "Users" },
    { name: "roles", label: "Roles" },
    { name: "contacts", label: "Contacts" },
    { name: "products", label: "Products" },
    { name: "parties", label: "Parties" },
    { name: "posts", label: "Posts" },
    { name: "dashboard", label: "Dashboard" },
    { name: "profile", label: "Profile" },
  ];

  const availableActions = [
    { value: "create", label: "Create" },
    { value: "read", label: "Read" },
    { value: "update", label: "Update" },
    { value: "delete", label: "Delete" },
    { value: "view", label: "View" },
  ];

  const [validationErrors, setValidationErrors] = useState({});
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Handle window resize for responsive design
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  usePermissions();
  const hasLoadedRoles = useRef(false);

  // Check permission directly from Redux state to avoid infinite loops
  const canReadRoles = useMemo(() => {
    if (user?.role === "admin") return true;
    const userRole = activeRoles.find((role) => role.name === user?.role);
    if (!userRole) return false;
    const permission = userRole.permissions.find((p) => p.resource === "roles");
    return permission && permission.actions.includes("read");
  }, [user?.role, activeRoles]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/login");
      return;
    }

    if (!canReadRoles) {
      navigate("/my-business");
      return;
    }

    // Only load roles once when component mounts and user is authenticated
    if (!hasLoadedRoles.current) {
      dispatch(getAllRoles());
      dispatch(getActiveRoles());
      hasLoadedRoles.current = true;
    }
  }, [dispatch, isAuthenticated, navigate, canReadRoles]);

  useEffect(() => {
    if (createSuccess) {
      dispatch(resetCreate());
      handleCloseModal();
      // Reload roles after successful creation
      hasLoadedRoles.current = false;
      dispatch(getAllRoles());
      dispatch(getActiveRoles());
      hasLoadedRoles.current = true;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- handleCloseModal stable
  }, [dispatch, createSuccess]);

  useEffect(() => {
    if (updateSuccess) {
      dispatch(resetUpdate());
      handleCloseModal();
      // Reload roles after successful update
      hasLoadedRoles.current = false;
      dispatch(getAllRoles());
      dispatch(getActiveRoles());
      hasLoadedRoles.current = true;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- handleCloseModal stable
  }, [dispatch, updateSuccess]);

  const handleOpenModal = (role = null) => {
    if (role) {
      setEditingRole(role);
      setFormData({
        name: role.name || "",
        description: role.description || "",
        permissions: role.permissions || [],
        isActive: role.isActive !== undefined ? role.isActive : true,
      });
    } else {
      setEditingRole(null);
      setFormData({
        name: "",
        description: "",
        permissions: [],
        isActive: true,
      });
    }
    setShowModal(true);
    setValidationErrors({});
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingRole(null);
    setFormData({
      name: "",
      description: "",
      permissions: [],
      isActive: true,
    });
    setValidationErrors({});
    dispatch(clearCurrentRole());
  };

  const handleOpenDeleteModal = (roleId) => {
    setDeleteRoleId(roleId);
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setDeleteRoleId(null);
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = "Role name is required";
    } else if (formData.name.trim().length < 2) {
      errors.name = "Role name must be at least 2 characters";
    } else if (formData.name.trim().length > 50) {
      errors.name = "Role name cannot exceed 50 characters";
    }

    if (formData.description && formData.description.length > 200) {
      errors.description = "Description cannot exceed 200 characters";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    if (validationErrors[name]) {
      setValidationErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  // Handle permission toggle
  const togglePermission = (resource, action) => {
    setFormData((prev) => {
      const permissions = [...(prev.permissions || [])];
      const resourceIndex = permissions.findIndex(
        (p) => p.resource === resource
      );

      if (resourceIndex > -1) {
        // Resource exists, toggle action
        const actions = [...permissions[resourceIndex].actions];
        const actionIndex = actions.indexOf(action);

        if (actionIndex > -1) {
          // Remove action
          actions.splice(actionIndex, 1);
          if (actions.length === 0) {
            // Remove resource if no actions left
            permissions.splice(resourceIndex, 1);
          } else {
            permissions[resourceIndex] = {
              ...permissions[resourceIndex],
              actions,
            };
          }
        } else {
          // Add action
          permissions[resourceIndex] = {
            ...permissions[resourceIndex],
            actions: [...actions, action],
          };
        }
      } else {
        // Add new resource with action
        permissions.push({
          resource,
          actions: [action],
        });
      }

      return {
        ...prev,
        permissions,
      };
    });
  };

  // Check if permission is selected
  const isPermissionSelected = (resource, action) => {
    const permission = formData.permissions.find((p) => p.resource === resource);
    return permission && permission.actions.includes(action);
  };

  // Select all actions for a resource
  const selectAllActions = (resource) => {
    setFormData((prev) => {
      const permissions = [...(prev.permissions || [])];
      const resourceIndex = permissions.findIndex(
        (p) => p.resource === resource
      );

      if (resourceIndex > -1) {
        permissions[resourceIndex] = {
          ...permissions[resourceIndex],
          actions: availableActions.map((a) => a.value),
        };
      } else {
        permissions.push({
          resource,
          actions: availableActions.map((a) => a.value),
        });
      }

      return {
        ...prev,
        permissions,
      };
    });
  };

  // Clear all actions for a resource
  const clearAllActions = (resource) => {
    setFormData((prev) => {
      const permissions = prev.permissions.filter((p) => p.resource !== resource);
      return {
        ...prev,
        permissions,
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      if (editingRole) {
        await dispatch(
          updateRole({
            roleId: editingRole._id,
            roleData: formData,
          })
        ).unwrap();
      } else {
        await dispatch(createRole(formData)).unwrap();
      }
    } catch (err) {
      // Error is handled by the slice with toast
    }
  };

  const handleDelete = async () => {
    try {
      await dispatch(deleteRole(deleteRoleId)).unwrap();
      handleCloseDeleteModal();
      dispatch(getAllRoles());
    } catch (err) {
      // Error is handled by the slice with toast
    }
  };

  // eslint-disable-next-line no-unused-vars
  const _handleView = async (roleId) => {
    try {
      await dispatch(getRoleById(roleId)).unwrap();
      const role = roles.find((r) => r._id === roleId);
      if (role) {
        handleOpenModal(role);
      }
    } catch (err) {
      // Error is handled by the slice with toast
    }
  };

  const filteredRoles = roles.filter(
    (role) =>
      role.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (role.description &&
        role.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div style={styles.container}>
      <div style={{
        ...styles.content,
        padding: isMobile ? "16px" : "24px",
        paddingBottom: isMobile ? "100px" : "100px",
      }}>
        {/* Page Header */}
        <div style={{
          ...styles.pageHeader,
          marginBottom: isMobile ? "16px" : "24px",
        }}>
          <h1 style={{
            ...styles.pageTitle,
            fontSize: isMobile ? "28px" : "32px",
          }}>Role List</h1>
        </div>

        {/* Search Bar */}
        <div style={{
          ...styles.searchContainer,
          marginBottom: isMobile ? "16px" : "20px",
          padding: isMobile ? "8px" : "12px",
        }}>
          <div style={{ position: "relative", width: "100%" }}>
            <div style={{
              position: "absolute",
              left: "16px",
              top: "50%",
              transform: "translateY(-50%)",
              color: "#667781",
              fontSize: "16px",
              pointerEvents: "none",
              zIndex: 1,
            }}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z" fill="currentColor"/>
              </svg>
            </div>
            <input
              type="text"
              placeholder="Search roles..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={styles.searchInput}
            />
          </div>
        </div>

        {/* Role Cards */}
        {loading ? (
          <div style={styles.loadingContainer}>
            <p>Loading roles...</p>
          </div>
        ) : (
          <div style={{
            ...styles.rolesContainer,
            gap: isMobile ? "12px" : "16px",
          }}>
            {filteredRoles.length === 0 ? (
              <div style={{
                ...styles.noRoles,
                padding: isMobile ? "60px 20px" : "80px 40px",
              }}>
                <div style={{ fontSize: "64px", marginBottom: "16px", opacity: 0.6 }}>👥</div>
                <p style={{ margin: "0 0 24px 0", fontSize: "17px", fontWeight: "400", color: "#667781" }}>
                  No roles found
                </p>
                <PermissionGate resource="roles" action="create">
                  <button
                    onClick={() => handleOpenModal()}
                    style={styles.createFirstButton}
                  >
                    Create First Role
                  </button>
                </PermissionGate>
              </div>
            ) : (
              filteredRoles.map((role) => (
                <div 
                  key={role._id} 
                  style={{
                    ...styles.roleCard,
                    padding: isMobile ? "16px" : "20px",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.12)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.08)";
                  }}
                >
                  <div style={styles.roleCardContent}>
                    <div style={styles.roleInfo}>
                      <div style={{
                        ...styles.roleName,
                        fontSize: isMobile ? "16px" : "18px",
                      }}>{role.name.toUpperCase()}</div>
                      <div style={styles.roleDetails}>
                        {role.description && (
                          <div style={styles.roleDetailRow}>
                            <span style={styles.roleDetailValue}>{role.description}</span>
                          </div>
                        )}
                        <div style={styles.roleDetailRow}>
                          <span style={styles.roleDetailLabel}>Permissions: </span>
                          <span style={styles.roleDetailValue}>{role.permissions?.length || 0} permission(s)</span>
                        </div>
                        <div style={styles.roleDetailRow}>
                          <span style={styles.roleDetailLabel}>Status: </span>
                          <span style={{
                            ...styles.roleStatusBadge,
                            backgroundColor: role.isActive ? "#25d366" : "#8696a0",
                          }}>{role.isActive ? "Active" : "Inactive"}</span>
                        </div>
                      </div>
                    </div>
                    <div style={styles.roleActions}>
                      <PermissionGate resource="roles" action="update">
                        <button
                          onClick={() => handleOpenModal(role)}
                          style={styles.roleEditButton}
                          title="Edit Role"
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = "#f0f2f5";
                            e.currentTarget.style.transform = "scale(1.05)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = "#ffffff";
                            e.currentTarget.style.transform = "scale(1)";
                          }}
                        >
                          ✏️
                        </button>
                      </PermissionGate>
                      <PermissionGate resource="roles" action="delete">
                        <button
                          onClick={() => handleOpenDeleteModal(role._id)}
                          style={styles.roleDeleteButton}
                          title="Delete Role"
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = "#fff5f5";
                            e.currentTarget.style.transform = "scale(1.05)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = "#ffffff";
                            e.currentTarget.style.transform = "scale(1)";
                          }}
                        >
                          🗑️
                        </button>
                      </PermissionGate>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Floating Action Button - Create Role - Positioned above bottom navbar */}
      <PermissionGate resource="roles" action="create">
        <button
          onClick={() => handleOpenModal()}
          style={{
            ...styles.fabButton,
            bottom: isMobile ? "90px" : "100px",
            borderRadius: isMobile ? "28px" : "32px",
            padding: isMobile ? "14px 24px" : "16px 32px",
            fontSize: isMobile ? "16px" : "18px",
            zIndex: 1001,
          }}
          title="Create Role"
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "translateX(-50%) translateY(-4px)";
            e.currentTarget.style.boxShadow = "0 6px 20px rgba(220, 53, 69, 0.5)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "translateX(-50%) translateY(0)";
            e.currentTarget.style.boxShadow = "0 4px 16px rgba(220, 53, 69, 0.4)";
          }}
        >
          <span style={{
            ...styles.fabIcon,
            fontSize: isMobile ? "20px" : "24px",
          }}>👤</span>
          <span style={styles.fabText}>Create Role</span>
        </button>
      </PermissionGate>

      {/* Create/Edit Modal */}
      {showModal && (
        <div 
          style={{
            ...styles.modalOverlay,
            alignItems: isMobile ? "flex-start" : "center",
            zIndex: 1002,
            paddingBottom: isMobile ? "100px" : "100px",
          }} 
          onClick={handleCloseModal}
        >
          <div 
            style={{
              ...styles.modal,
              borderRadius: isMobile ? "0" : "8px",
              padding: isMobile ? "20px 16px" : "0",
              maxWidth: isMobile ? "100%" : "800px",
              width: isMobile ? "100%" : "90%",
              maxHeight: isMobile ? "calc(100vh - 100px)" : "calc(90vh - 100px)",
              marginBottom: isMobile ? "100px" : "0",
              display: "flex",
              flexDirection: "column",
            }} 
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              ...styles.modalHeader,
              marginBottom: isMobile ? "16px" : "0",
            }}>
              <h3 style={{
                ...styles.modalTitle,
                fontSize: isMobile ? "20px" : "24px",
              }}>
                {editingRole ? "Edit Role" : "Create Role"}
              </h3>
              <button
                onClick={handleCloseModal}
                style={styles.closeButton}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#f0f2f5";
                  e.currentTarget.style.color = "#111b21";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "transparent";
                  e.currentTarget.style.color = "#667781";
                }}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit} style={{
              ...styles.form,
              gap: isMobile ? "16px" : "20px",
              display: "flex",
              flexDirection: "column",
              flex: 1,
              overflowY: "auto",
              padding: isMobile ? "0" : "20px",
            }}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Role Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  style={{
                    ...styles.input,
                    borderColor: validationErrors.name ? "#dc3545" : "#e9edef",
                    width: "100%",
                  }}
                  placeholder="Enter role name"
                  disabled={createLoading || updateLoading}
                />
                {validationErrors.name && (
                  <span style={styles.errorText}>
                    {validationErrors.name}
                  </span>
                )}
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  style={{
                    ...styles.textarea,
                    borderColor: validationErrors.description ? "#dc3545" : "#e9edef",
                    width: "100%",
                  }}
                  placeholder="Enter role description"
                  rows="3"
                  disabled={createLoading || updateLoading}
                />
                {validationErrors.description && (
                  <span style={styles.errorText}>
                    {validationErrors.description}
                  </span>
                )}
              </div>

              <div style={styles.formGroup}>
                <label style={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    name="isActive"
                    checked={formData.isActive}
                    onChange={handleChange}
                    disabled={createLoading || updateLoading}
                  />
                  Active
                </label>
              </div>

              {/* Permissions Section */}
              <div style={styles.formGroup}>
                <label style={styles.label}>Permissions</label>
                <div style={styles.permissionsContainer}>
                  {availableResources.map((resource) => (
                    <div key={resource.name} style={styles.permissionResource}>
                      <div style={styles.resourceHeader}>
                        <h4 style={styles.resourceTitle}>{resource.label}</h4>
                        <div style={styles.resourceActions}>
                          <button
                            type="button"
                            onClick={() => selectAllActions(resource.name)}
                            style={styles.selectAllButton}
                            disabled={createLoading || updateLoading}
                          >
                            Select All
                          </button>
                          <button
                            type="button"
                            onClick={() => clearAllActions(resource.name)}
                            style={styles.clearAllButton}
                            disabled={createLoading || updateLoading}
                          >
                            Clear
                          </button>
                        </div>
                      </div>
                      <div style={styles.actionsContainer}>
                        {availableActions.map((action) => (
                          <button
                            key={action.value}
                            type="button"
                            onClick={() =>
                              togglePermission(resource.name, action.value)
                            }
                            style={{
                              ...styles.permissionButton,
                              ...(isPermissionSelected(
                                resource.name,
                                action.value
                              )
                                ? styles.permissionButtonActive
                                : {}),
                            }}
                            disabled={createLoading || updateLoading}
                          >
                            {action.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div style={{
                ...styles.modalActions,
                marginTop: isMobile ? "8px" : "16px",
                flexWrap: isMobile ? "wrap" : "nowrap",
              }}>
                <button
                  type="button"
                  onClick={handleCloseModal}
                  style={{
                    ...styles.cancelButton,
                    padding: isMobile ? "12px 20px" : "12px 24px",
                    flex: isMobile ? "1" : "none",
                    minWidth: isMobile ? "0" : "100px",
                  }}
                  disabled={createLoading || updateLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    ...styles.submitButton,
                    padding: isMobile ? "12px 20px" : "12px 24px",
                    flex: isMobile ? "1" : "none",
                    minWidth: isMobile ? "0" : "140px",
                  }}
                  disabled={createLoading || updateLoading}
                >
                  {createLoading || updateLoading
                    ? "Saving..."
                    : editingRole
                    ? "Update Role"
                    : "Create Role"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div
          style={{
            ...styles.modalOverlay,
            alignItems: isMobile ? "flex-start" : "center",
            zIndex: 1002,
            paddingBottom: isMobile ? "100px" : "100px",
          }}
          onClick={handleCloseDeleteModal}
        >
          <div 
            style={{
              ...styles.modal,
              borderRadius: isMobile ? "0" : "8px",
              padding: isMobile ? "20px 16px" : "30px",
              maxWidth: isMobile ? "100%" : "500px",
              width: isMobile ? "100%" : "90%",
              marginBottom: isMobile ? "100px" : "0",
              maxHeight: isMobile ? "calc(100vh - 100px)" : "calc(90vh - 100px)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              ...styles.modalHeader,
              marginBottom: isMobile ? "16px" : "24px",
            }}>
              <h3 style={{
                ...styles.modalTitle,
                fontSize: isMobile ? "20px" : "24px",
              }}>Confirm Delete</h3>
              <button
                onClick={handleCloseDeleteModal}
                style={styles.closeButton}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#f0f2f5";
                  e.currentTarget.style.color = "#111b21";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "transparent";
                  e.currentTarget.style.color = "#667781";
                }}
              >
                ×
              </button>
            </div>
            <div style={{
              ...styles.deleteModalContent,
              padding: 0,
              marginBottom: "24px",
            }}>
              <p style={{
                margin: "0 0 12px 0",
                fontSize: "14px",
                color: "#667781",
                lineHeight: "20px",
                fontWeight: "400",
              }}>Are you sure you want to delete this role?</p>
              <p style={{
                ...styles.warningText,
                margin: 0,
                fontSize: "14px",
                color: "#dc3545",
                fontWeight: "400",
              }}>
                This action cannot be undone.
              </p>
            </div>
            <div style={{
              ...styles.modalActions,
              marginTop: 0,
              flexWrap: isMobile ? "wrap" : "nowrap",
            }}>
              <button
                onClick={handleCloseDeleteModal}
                style={{
                  ...styles.cancelButton,
                  padding: isMobile ? "12px 20px" : "12px 24px",
                  flex: isMobile ? "1" : "none",
                  minWidth: isMobile ? "0" : "100px",
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                style={{
                  ...styles.deleteConfirmButton,
                  padding: isMobile ? "12px 20px" : "12px 24px",
                  flex: isMobile ? "1" : "none",
                  minWidth: isMobile ? "0" : "100px",
                }}
                disabled={loading}
              >
                {loading ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
      <BottomNavbar />
    </div>
  );
};

const styles = {
  container: {
    minHeight: "100vh",
    backgroundColor: "#f0f2f5",
    display: "flex",
    flexDirection: "column",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Helvetica Neue', Helvetica, 'Lucida Grande', Arial, Ubuntu, Cantarell, 'Fira Sans', sans-serif",
    paddingBottom: "80px",
  },
  content: {
    maxWidth: "1200px",
    margin: "0 auto",
    padding: "24px",
    paddingBottom: "100px",
    minHeight: "calc(100vh - 80px)",
  },
  pageHeader: {
    marginBottom: "24px",
    paddingBottom: "16px",
  },
  pageTitle: {
    fontSize: "32px",
    fontWeight: "bold",
    color: "#111b21",
    margin: 0,
    letterSpacing: "-0.02em",
  },
  searchContainer: {
    marginBottom: "20px",
    padding: "12px",
  },
  searchInput: {
    width: "100%",
    padding: "12px 16px 12px 44px",
    border: "1px solid #e9edef",
    borderRadius: "24px",
    backgroundColor: "#ffffff",
    fontSize: "15px",
    fontWeight: "400",
    color: "#111b21",
    outline: "none",
    transition: "border-color 0.2s",
    letterSpacing: "-0.01em",
  },
  rolesContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    marginBottom: "20px",
  },
  roleCard: {
    backgroundColor: "#ffeef2",
    borderRadius: "12px",
    padding: "20px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
    border: "1px solid #ffd6e0",
    transition: "transform 0.2s, box-shadow 0.2s",
  },
  roleCardContent: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "12px",
  },
  roleInfo: {
    flex: 1,
    minWidth: 0,
  },
  roleName: {
    fontSize: "18px",
    fontWeight: "bold",
    color: "#111b21",
    marginBottom: "8px",
    letterSpacing: "-0.01em",
    lineHeight: "22px",
    textTransform: "uppercase",
  },
  roleDetails: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  roleDetailRow: {
    fontSize: "14px",
    fontWeight: "400",
    color: "#667781",
    letterSpacing: "-0.01em",
    lineHeight: "20px",
  },
  roleDetailLabel: {
    fontWeight: "500",
    color: "#667781",
  },
  roleDetailValue: {
    color: "#111b21",
  },
  roleStatusBadge: {
    padding: "4px 10px",
    borderRadius: "12px",
    color: "#ffffff",
    fontSize: "12px",
    fontWeight: "500",
    display: "inline-block",
  },
  roleActions: {
    display: "flex",
    gap: "8px",
    flexShrink: 0,
  },
  roleEditButton: {
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    backgroundColor: "#ffffff",
    border: "1px solid #e9edef",
    cursor: "pointer",
    fontSize: "18px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "background-color 0.2s, transform 0.2s",
    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
  },
  roleDeleteButton: {
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    backgroundColor: "#ffffff",
    border: "1px solid #e9edef",
    cursor: "pointer",
    fontSize: "18px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "background-color 0.2s, transform 0.2s",
    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
  },
  noRoles: {
    padding: "80px 40px",
    textAlign: "center",
    color: "#667781",
  },
  createFirstButton: {
    padding: "12px 24px",
    backgroundColor: "#dc3545",
    color: "#ffffff",
    border: "none",
    borderRadius: "24px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "500",
    transition: "background-color 0.2s",
    boxShadow: "0 2px 8px rgba(220, 53, 69, 0.3)",
  },
  modalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.65)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1002,
    overflowY: "auto",
    paddingBottom: "100px",
  },
  modal: {
    backgroundColor: "#ffffff",
    borderRadius: "8px",
    width: "90%",
    maxWidth: "800px",
    maxHeight: "calc(90vh - 100px)",
    overflow: "auto",
    boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
    marginBottom: "100px",
  },
  fabButton: {
    position: "fixed",
    bottom: "100px",
    left: "50%",
    transform: "translateX(-50%)",
    backgroundColor: "#dc3545",
    color: "#ffffff",
    border: "none",
    borderRadius: "32px",
    padding: "16px 32px",
    cursor: "pointer",
    fontSize: "18px",
    fontWeight: "500",
    display: "flex",
    alignItems: "center",
    gap: "12px",
    boxShadow: "0 4px 16px rgba(220, 53, 69, 0.4)",
    zIndex: 1001,
    transition: "transform 0.2s, box-shadow 0.2s",
  },
  fabIcon: {
    fontSize: "24px",
    lineHeight: "1",
  },
  fabText: {
    letterSpacing: "-0.01em",
    whiteSpace: "nowrap",
  },
  modalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "20px",
    borderBottom: "1px solid #e9edef",
  },
  modalTitle: {
    fontSize: "20px",
    fontWeight: "500",
    color: "#111b21",
    margin: 0,
    letterSpacing: "-0.01em",
  },
  closeButton: {
    background: "none",
    border: "none",
    fontSize: "32px",
    color: "#667781",
    cursor: "pointer",
    padding: 0,
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "background-color 0.2s, color 0.2s",
    lineHeight: "1",
    fontWeight: "300",
  },
  form: {
    padding: "20px",
    display: "flex",
    flexDirection: "column",
    gap: "20px",
  },
  formGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    width: "100%",
  },
  label: {
    display: "block",
    fontSize: "14px",
    fontWeight: "500",
    color: "#111b21",
    letterSpacing: "-0.01em",
  },
  input: {
    width: "100%",
    padding: "12px 16px",
    fontSize: "15px",
    fontWeight: "400",
    border: "1px solid #e9edef",
    borderRadius: "8px",
    backgroundColor: "#ffffff",
    outline: "none",
    transition: "border-color 0.2s",
    color: "#111b21",
    letterSpacing: "-0.01em",
    boxSizing: "border-box",
  },
  textarea: {
    width: "100%",
    padding: "12px 16px",
    fontSize: "15px",
    fontWeight: "400",
    border: "1px solid #e9edef",
    borderRadius: "8px",
    backgroundColor: "#ffffff",
    outline: "none",
    transition: "border-color 0.2s",
    color: "#111b21",
    letterSpacing: "-0.01em",
    boxSizing: "border-box",
    fontFamily: "inherit",
    resize: "vertical",
    lineHeight: "20px",
  },
  inputError: {
    borderColor: "#dc3545",
  },
  errorText: {
    color: "#dc3545",
    fontSize: "12px",
    marginTop: "4px",
    display: "block",
    fontWeight: "400",
  },
  checkboxLabel: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    cursor: "pointer",
  },
  permissionsContainer: {
    marginTop: "12px",
    maxHeight: "400px",
    overflowY: "auto",
    border: "1px solid #e9edef",
    borderRadius: "8px",
    padding: "16px",
    backgroundColor: "#f0f2f5",
  },
  permissionResource: {
    marginBottom: "24px",
    paddingBottom: "16px",
    borderBottom: "1px solid #e9edef",
  },
  resourceHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "12px",
  },
  resourceTitle: {
    fontSize: "16px",
    fontWeight: "500",
    color: "#111b21",
    margin: 0,
    letterSpacing: "-0.01em",
  },
  resourceActions: {
    display: "flex",
    gap: "8px",
  },
  selectAllButton: {
    padding: "6px 12px",
    backgroundColor: "#00a884",
    color: "#ffffff",
    border: "none",
    borderRadius: "24px",
    cursor: "pointer",
    fontSize: "12px",
    fontWeight: "400",
    transition: "background-color 0.2s",
    boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
  },
  clearAllButton: {
    padding: "6px 12px",
    backgroundColor: "#f0f2f5",
    color: "#111b21",
    border: "none",
    borderRadius: "24px",
    cursor: "pointer",
    fontSize: "12px",
    fontWeight: "400",
    transition: "background-color 0.2s",
  },
  actionsContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: "8px",
  },
  permissionButton: {
    padding: "8px 16px",
    backgroundColor: "#ffffff",
    color: "#111b21",
    border: "1px solid #e9edef",
    borderRadius: "24px",
    cursor: "pointer",
    fontSize: "13px",
    fontWeight: "400",
    transition: "all 0.2s",
    minWidth: "80px",
    letterSpacing: "-0.01em",
  },
  permissionButtonActive: {
    backgroundColor: "#25d366",
    color: "#ffffff",
    borderColor: "#25d366",
    boxShadow: "0 1px 2px rgba(37, 211, 102, 0.3)",
  },
  modalActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "12px",
    paddingTop: "16px",
    paddingBottom: "20px",
    borderTop: "1px solid #e9edef",
    marginTop: "16px",
    position: "sticky",
    bottom: 0,
    backgroundColor: "#ffffff",
    zIndex: 1,
  },
  cancelButton: {
    padding: "12px 24px",
    backgroundColor: "#f0f2f5",
    color: "#111b21",
    border: "none",
    borderRadius: "24px",
    cursor: "pointer",
    fontSize: "15px",
    fontWeight: "400",
    transition: "background-color 0.2s",
    minWidth: "100px",
  },
  submitButton: {
    padding: "12px 24px",
    backgroundColor: "#dc3545",
    color: "#ffffff",
    border: "none",
    borderRadius: "24px",
    cursor: "pointer",
    fontSize: "15px",
    fontWeight: "400",
    transition: "background-color 0.2s",
    minWidth: "140px",
    boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
  },
  deleteModalContent: {
    padding: 0,
    marginBottom: "24px",
  },
  warningText: {
    color: "#dc3545",
    fontWeight: "400",
    marginTop: "8px",
    fontSize: "14px",
    lineHeight: "20px",
  },
  deleteConfirmButton: {
    padding: "12px 24px",
    backgroundColor: "#dc3545",
    color: "#ffffff",
    border: "none",
    borderRadius: "24px",
    cursor: "pointer",
    fontSize: "15px",
    fontWeight: "400",
    transition: "background-color 0.2s",
    minWidth: "100px",
    boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
  },
  loadingContainer: {
    textAlign: "center",
    padding: "40px",
    color: "#999",
  },
};

export default RoleManagement;

